import React, { useEffect, useState } from "react";
import { CheckCircle2, XCircle, HelpCircle, TrendingUp } from "lucide-react";
import api from "../../lib/api";

/**
 * ApprovalDeltaChart — weekly bar chart of approve/reject/more_info decisions
 * per admin, over the last N weeks (default 8).
 */
export default function ApprovalDeltaChart({ weeks = 8 }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/admin/analytics/approval-deltas?weeks=${weeks}`);
        setData(res.data);
      } catch (e) { /* silent */ }
    })();
  }, [weeks]);

  if (!data) return null;
  const admins = data.admins || [];
  const maxTotal = Math.max(1, ...admins.flatMap((a) => a.weeks.map((w) => w.total)));

  return (
    <div className="border border-navy/10 bg-white rounded-lg" data-testid="approval-delta-chart">
      <div className="px-5 py-3 border-b border-navy/10 bg-navy/[0.02] flex items-center justify-between">
        <div className="overline flex items-center gap-2 text-emerald-700">
          <TrendingUp size={12} /> Approval Delta Tracker
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
          Last {data.weeks_covered} weeks
        </div>
      </div>
      <div className="p-5 space-y-5">
        {admins.length === 0 && (
          <div className="text-sm text-ink-muted italic">No approval activity in this window.</div>
        )}
        {admins.map((a) => (
          <div key={a.admin_email} data-testid={`admin-row-${a.admin_email}`}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-sm text-ink font-heading truncate">{a.admin_email}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                {a.total} decisions
              </div>
            </div>
            <div className="flex items-end gap-1 h-16">
              {a.weeks.map((w) => {
                const total = w.total || 0;
                const h = (total / maxTotal) * 100;
                const app = (w.approve || 0) / Math.max(1, total) * h;
                const rej = (w.reject || 0) / Math.max(1, total) * h;
                const mi = (w.more_info || 0) / Math.max(1, total) * h;
                const other = h - app - rej - mi;
                return (
                  <div
                    key={w.week}
                    className="flex-1 flex flex-col justify-end min-w-0 rounded-sm overflow-hidden border border-navy/10"
                    title={`${w.week}: approve ${w.approve || 0} · reject ${w.reject || 0} · more_info ${w.more_info || 0}`}
                  >
                    <div className="bg-navy/20" style={{ height: `${other}%` }} />
                    <div className="bg-amber-400" style={{ height: `${mi}%` }} />
                    <div className="bg-rose-500" style={{ height: `${rej}%` }} />
                    <div className="bg-emerald-600" style={{ height: `${app}%` }} />
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between text-[9px] font-mono text-ink-muted mt-1">
              <span>{a.weeks[0]?.week}</span>
              <span>{a.weeks[a.weeks.length - 1]?.week}</span>
            </div>
          </div>
        ))}
        <div className="flex items-center gap-3 pt-3 border-t border-navy/10 text-[11px]">
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <CheckCircle2 size={11} /> Approve
          </span>
          <span className="inline-flex items-center gap-1 text-rose-600">
            <XCircle size={11} /> Reject
          </span>
          <span className="inline-flex items-center gap-1 text-amber-600">
            <HelpCircle size={11} /> More info
          </span>
        </div>
      </div>
    </div>
  );
}
